import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { notifyUnauthorized } from './authStore';
import { authFailureEndsSession } from './sessionExpiry';

/**
 * ONE place where a dead session is noticed, for the WHOLE CRM.
 *
 * Every screen's data goes through TanStack — 40-odd `useQuery` call sites and
 * 26 `useMutation`s across quotes, accounts, billing, metering, market ops,
 * broker, settings — including the raw-`fetch` fetchers that throw the same
 * `ApiError` without ever calling an api-client callback (bill PDF, broker
 * licence upload, template validate). Attaching the check to the caches means
 * a new module inherits sign-out-on-expiry by existing, instead of by
 * remembering to wire something.
 *
 * `authFailureEndsSession` owns the decision and is deliberately conservative —
 * read the header of ./sessionExpiry before widening it. In particular this is
 * NOT "any 401 logs you out": that rule already exists, per-client, in
 * apiClient.ts, precisely so the advisory clients can opt out of it.
 */
function endSessionIfDead(error: unknown): void {
  const reason = authFailureEndsSession(error);
  if (reason !== null) notifyUnauthorized(reason);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: endSessionIfDead }),
    mutationCache: new MutationCache({ onError: endSessionIfDead }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}
